import { useAuthenticatedUserContext } from "@/context/AuthenticatedUserContext"
import { CLUSTER_SHARING_GROUP_TYPES, type RelatedCluster } from "@/domain/cluster/constants"
import { getDefaultRelatedCluster, getRelatedCluster } from "@/domain/cluster/getRelatedCluster"
import { CollaboratorRole } from "@/domain/collaborator/constants"
import { getUserDefaultCollaborator } from "@/domain/collaborator/getRelatedCollaborator"
import { is_source_mybarnes } from "@/domain/synchro/constants"
import { useGetSharingGroupClusters } from "@/hooks/useGetSharingGroupClusters"
import { isBlank, isNullOrUndefined } from "@/utils/ts-utils"
import { useMemo } from "react"
import useGetUserRoles from "./useGetUserRoles"

export default () => {
  const { userInfos } = useAuthenticatedUserContext()

  const { hasRoleGuru } = useGetUserRoles()

  const {
    userClusters,
    userClustersIds,
    userClustersByRole,
    userClustersIdsByRole,
    defaultClusterId,
    defaultCluster,
  } = useMemo(() => {
    const userDftCollaborator = getUserDefaultCollaborator(userInfos?.collaborators)
    const userDftCollaboratorDftCluster = getDefaultRelatedCluster(userDftCollaborator?.cluster)

    // If a default collab is defined (and his default cluster too), defaultCluster can be set
    let defaultCluster: RelatedCluster | undefined = userDftCollaboratorDftCluster
    let defaultClusterId: string | undefined = userDftCollaboratorDftCluster?.id

    const userClusters = [] as RelatedCluster[]
    const userClustersByRole = {} as { [role: string]: RelatedCluster[] }
    const userClustersIds = [] as string[]
    const userClustersIdsByRole = {} as { [role: string]: string[] }

    for (const co of userInfos?.collaborators ?? []) {
      for (const cl of co.cluster ?? []) {
        if (isNullOrUndefined(cl.cluster)) continue

        const role = cl.role?.code ?? CollaboratorRole.NONE
        const relatedCluster = getRelatedCluster(cl)
        const relatedClusterId = relatedCluster.id

        // If no default collab / cluster defined before, set it with the first one found
        if (isBlank(defaultClusterId)) {
          defaultClusterId = relatedClusterId
          defaultCluster = relatedCluster
        }

        if (!userClustersIds.includes(relatedClusterId)) {
          userClustersIds.push(relatedClusterId)
          userClusters.push(relatedCluster)
        }

        if (isNullOrUndefined(userClustersIdsByRole[role])) userClustersIdsByRole[role] = []
        if (!userClustersIdsByRole[role]?.includes(relatedClusterId)) {
          userClustersIdsByRole[role]?.push(relatedClusterId)

          if (isNullOrUndefined(userClustersByRole[role])) userClustersByRole[role] = []
          userClustersByRole[role]?.push(relatedCluster)
        }
      }
    }

    return {
      userClusters,
      userClustersIds,
      userClustersByRole,
      userClustersIdsByRole,
      defaultClusterId,
      defaultCluster,
    }
  }, [userInfos])

  const {
    userCollaboratorsMyBarnes,
    userCollaboratorsMyBarnesIds,
    userCollaborators,
    userCollaboratorsIds,
    defaulCollaboratorId,
    defaulCollaborator,
  } = useMemo(() => {
    const userCollaboratorsMyBarnes =
      userInfos?.collaborators?.filter(co => is_source_mybarnes(co)) ?? []
    const userCollaboratorsMyBarnesIds = userCollaboratorsMyBarnes.map(co => co.id)

    const userCollaborators = userInfos?.collaborators ?? []
    const userCollaboratorsIds = userCollaborators.map(co => co.id)

    let defaulCollaborator = userCollaboratorsMyBarnes[0] ?? undefined
    let defaulCollaboratorId = userCollaboratorsIds[0] ?? undefined

    for (const co of userInfos?.collaborators ?? []) {
      if (co.isUserDefault) {
        defaulCollaborator = co
        defaulCollaboratorId = co.id
      }
    }

    return {
      userCollaboratorsMyBarnes,
      userCollaboratorsMyBarnesIds,
      userCollaborators,
      userCollaboratorsIds,
      defaulCollaboratorId,
      defaulCollaborator,
    }
  }, [userInfos])

  // Sharing groups (BARNES SAS, BARNES Provence...) the user belongs to through their BUs
  const userSharingGroupTypes = useMemo(() => {
    const types = new Set<string>()
    for (const cluster of userClusters) {
      for (const type of cluster.type ?? []) {
        if (type.code && CLUSTER_SHARING_GROUP_TYPES.includes(type.code)) types.add(type.code)
      }
    }
    return [...types].sort()
  }, [userClusters])

  const { clusterIds: sharingGroupClusterIds } = useGetSharingGroupClusters(userSharingGroupTypes)

  const isPartOfSharingGroup = useMemo(
    () => hasRoleGuru() || userSharingGroupTypes.length > 0,
    [hasRoleGuru, userSharingGroupTypes]
  )

  const allUserClusterIds = useMemo(() => {
    return hasRoleGuru() ? [] : [...new Set([...userClustersIds, ...sharingGroupClusterIds])]
  }, [hasRoleGuru, userClustersIds, sharingGroupClusterIds])

  return {
    // ...userData,
    data: userInfos,
    clusters: userClusters,
    clusterIds: userClustersIds,
    clustersByRole: userClustersByRole,
    clusterIdsByRole: userClustersIdsByRole,
    clusterIds_all: allUserClusterIds,
    defaultClusterId,
    defaultCluster,
    collaboratorsMyBarnes: userCollaboratorsMyBarnes,
    collaboratorsMyBarnesIds: userCollaboratorsMyBarnesIds,
    collaborators: userCollaborators,
    collaboratorsIds: userCollaboratorsIds,
    defaulCollaboratorId,
    defaulCollaborator,
    isPartOfSharingGroup,
  }
}
